import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit3, Plus, Search, Package, Check, Trash2, GraduationCap, X, Percent, History, AlertTriangle } from "lucide-react";
import { EditProductDialog } from "./EditProductDialog";
import { PaymentPlanDialog, PaymentPlanSaveData } from "./PaymentPlanDialog";
import { ZendeskTicketOnOrderDialog, ZendeskTicketOnOrderSaveData } from "./ZendeskTicketOnOrderDialog";
import { isNewOrderZendeskTicketEnabled, isNewOrderZendeskTicketClientCardVisible } from "@/features/newOrderZendeskTicketFeature";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clientService, ClientProduct, ClientAdjustment } from "@/api/clientService";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { formatCurrency } from "@/lib/formatters";
import { clientsApi, profileClientApi, paymentConfigurationsApi, paymentPlansApi, isCustomPaymentPlan } from "@/lib/api";
import { RemoveProductDialog } from "./RemoveProductDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useDebounce } from "@/hooks/useDebounce";
import { toast } from "@/components/ui/sonner";
import { codeSeries, digitalProducts, mapEtapaToGradeName } from "@/lib/utils";

interface ClientManageProps {
  client: {
    id: number;
    name: string;
    shipping_fee?: number;
    clientAdjustment?: number;
    paymentPlanId?: number;
    maxInstallments?: number;
    paymentConfigurationIds?: string[];
    createZendeskTicketOnOrder?: boolean;
  };
  products: ClientProduct[];
  loading: boolean;
  onProductUpdate: () => void;
}
type SeriesGroup = {
  seriesName: string;
  totalWithDiscount: number;
  dados: ClientProduct[];
};

interface ProfileClient { }

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string; message?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
    if (response?.data?.message) return response.data.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export function ClientManage({ client, products, loading, onProductUpdate }: ClientManageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [managedProducts, setManagedProducts] = useState<ClientProduct[]>(products);
  const [managedClient, setManagedClient] = useState(client);

  useEffect(() => {
    setManagedProducts(products);
  }, [products]);

  useEffect(() => {
    setManagedClient(client);
  }, [client]);

  const [selectedProduct, setSelectedProduct] = useState<ClientProduct | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [removingProduct, setRemovingProduct] = useState<any | ProfileClient | null>(null);
  const [searchTermProduct, setSearchTermProduct] = useState("");

  const debouncedSearchTermProduct = useDebounce(searchTermProduct, 300);
  const [isShippingFeeDialogOpen, setIsShippingFeeDialogOpen] = useState(false);
  const [shippingFee, setShippingFee] = useState(client.shipping_fee?.toString() || "0");
  const [shippingFeeComment, setShippingFeeComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Reajuste do Cliente com useQuery (sem cache)
  const currentYear = new Date().getFullYear();
  const [currentClientAdjustment, setCurrentClientAdjustment] = useState<number>(client.clientAdjustment || 0);
  const [isAdjustmentDialogOpen, setIsAdjustmentDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [adjustmentFormYear, setAdjustmentFormYear] = useState<string>(currentYear.toString());
  const [adjustmentFormValue, setAdjustmentFormValue] = useState<string>((client.clientAdjustment || 0).toString());
  const [adjustmentFormComment, setAdjustmentFormComment] = useState("");
  const [isLoadingAdjustment, setIsLoadingAdjustment] = useState(false);

  const {
    data: clientAdjustments = [],
    isLoading: isLoadingClientAdjustments,
    refetch: refetchClientAdjustments,
  } = useQuery({
    queryKey: ['clientAdjustments', client.id],
    queryFn: () => clientService.getClientAdjustments(client.id),
    enabled: !!client.id,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (clientAdjustments && clientAdjustments.length > 0) {
      const matchingYear = clientAdjustments.find((a) => (a.cycleYear ?? a.year) === currentYear);
      if (matchingYear) {
        setCurrentClientAdjustment(matchingYear.adjustment);
      } else {
        setCurrentClientAdjustment(0);
      }
    } else if (client.clientAdjustment !== undefined) {
      setCurrentClientAdjustment(client.clientAdjustment);
    }
  }, [clientAdjustments, currentYear, client.clientAdjustment]);

  // Plano de Pagamentos
  const { data: paymentPlans = [], isLoading: isLoadingPaymentPlans } = useQuery({
    queryKey: ['paymentPlans'],
    queryFn: paymentPlansApi.getAll,
    staleTime: Infinity,
  });
  const { data: paymentConfigurations = [], isLoading: isLoadingPaymentConfigurations } = useQuery({
    queryKey: ['payment-configurations'],
    queryFn: paymentConfigurationsApi.getAll,
    staleTime: 1000 * 60 * 5,
  });
  const [isPaymentPlanDialogOpen, setIsPaymentPlanDialogOpen] = useState(false);
  const currentPaymentPlan = paymentPlans.find((p) => p.paymentPlanId === managedClient.paymentPlanId);

  const handleSavePaymentPlan = async ({ paymentPlanId, paymentConfigurationIds, comment }: PaymentPlanSaveData) => {
    try {
      await clientsApi.update(managedClient.id, { paymentPlanId, paymentConfigurationIds, maxInstallments: null, comment });

      setManagedClient((current) => ({
        ...current,
        paymentPlanId,
        maxInstallments: undefined,
        paymentConfigurationIds,
      }));

      toast.success("Sucesso!", { description: "O plano de pagamento foi atualizado." });
    } catch (error) {
      console.error("Erro ao salvar plano de pagamento:", error);
      toast.error("Erro ao salvar", { description: getApiErrorMessage(error, "Não foi possível atualizar o plano de pagamento.") });
      throw error;
    }
  };

  // Chamado no Zendesk ao criar pedido
  const [isZendeskTicketDialogOpen, setIsZendeskTicketDialogOpen] = useState(false);

  const handleSaveZendeskTicketOnOrder = async ({ createZendeskTicketOnOrder, comment }: ZendeskTicketOnOrderSaveData) => {
    try {
      await clientsApi.update(managedClient.id, { createZendeskTicketOnOrder, comment });

      setManagedClient((current) => ({ ...current, createZendeskTicketOnOrder }));

      toast.success("Sucesso!", {
        description: createZendeskTicketOnOrder
          ? "O chamado no Zendesk ao criar pedido foi ativado."
          : "O chamado no Zendesk ao criar pedido foi desativado.",
      });
    } catch (error) {
      console.error("Erro ao salvar chamado no Zendesk ao criar pedido:", error);
      toast.error("Erro ao salvar", { description: getApiErrorMessage(error, "Não foi possível atualizar a configuração do chamado.") });
      throw error;
    }
  };

  const handleOpenAdjustmentModal = () => {
    setAdjustmentFormYear(currentYear.toString());
    const matchingYear = clientAdjustments.find((a) => (a.cycleYear ?? a.year) === currentYear);
    setAdjustmentFormValue((matchingYear?.adjustment ?? currentClientAdjustment ?? 0).toString());
    setAdjustmentFormComment("");
    setIsAdjustmentDialogOpen(true);
  };

  const handleSaveClientAdjustment = async () => {
    setIsLoadingAdjustment(true);
    try {
      const yearVal = parseInt(adjustmentFormYear, 10);
      const adjVal = parseFloat(adjustmentFormValue.replace(',', '.'));

      if (isNaN(yearVal) || yearVal < 2000 || yearVal > 2100) {
        toast.warning("Ano inválido", { description: "Informe um ano do ciclo válido (ex: 2026)." });
        setIsLoadingAdjustment(false);
        return;
      }
      if (yearVal < currentYear) {
        toast.warning("Operação não permitida", { description: `Não é possível editar ou cadastrar reajustes de ciclos anteriores a ${currentYear}.` });
        setIsLoadingAdjustment(false);
        return;
      }
      if (isNaN(adjVal) || adjVal < -100 || adjVal > 100) {
        toast.warning("Valor inválido", { description: "O percentual de reajuste deve ser entre -100% e 100%." });
        setIsLoadingAdjustment(false);
        return;
      }

      // Ensure comment is provided
      if (!adjustmentFormComment.trim()) {
        toast.warning('Comentário obrigatório', { description: 'É necessário informar o motivo do reajuste.' });
        setIsLoadingAdjustment(false);
        return;
      }

      await clientService.saveClientAdjustment(managedClient.id, {
        cycleYear: yearVal,
        adjustment: adjVal,
        comment: adjustmentFormComment,
      });

      if (yearVal === currentYear) {
        setCurrentClientAdjustment(adjVal);
      }
      await refetchClientAdjustments();
      queryClient.invalidateQueries({ queryKey: ['clientAdjustments', managedClient.id] });
      onProductUpdate();

      toast.success("Sucesso!", { description: `Reajuste do cliente para o ciclo ${yearVal} foi atualizado para ${adjVal}%.` });
      setIsAdjustmentDialogOpen(false);
      setAdjustmentFormComment("");
    } catch (error: any) {
      console.error("Erro ao salvar reajuste do cliente:", error);
      const backendMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Não foi possível atualizar o reajuste do cliente.";
      toast.error("Erro ao salvar", { description: backendMessage });
    } finally {
      setIsLoadingAdjustment(false);
    }
  };

  function agruparPorUso(products: ClientProduct[]) {
    const output: any[] = [];

    // Função para processar Aluno ou Professor
    const processar = (tipo: "Aluno" | "Professor") => {
      // Filtra somente ativos e que contenham o tipo no nome
      const itens = products.filter(
        (p) =>
          (p as any).isActive &&
          p.materialUseName?.toLowerCase().includes(tipo.toLowerCase())
      );

      if (itens.length === 0) return;

      // ACV = somatório priceWithoutDiscount * contractualQuantity dos ativos
      const acv = itens.reduce((sum, p) => sum + ((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0)), 0);

      // Agrupar por série
      const series = Object.values(
        itens.reduce((acc, item) => {
          const key = item.seriesName ?? "Sem Série";

          if (!acc[key]) {
            acc[key] = {
              seriesName: key,
              totalWithDiscount: 0,
              dados: [],
            };
          }

          acc[key].dados.push(item);
          acc[key].totalWithDiscount += (item.priceWithoutDiscount ?? item.productPrice ?? 0) * (item.contractualQuantity ?? 0);

          return acc;
        }, {} as Record<string, SeriesGroup>)
      );

      series.sort((a, b) => {
        const codeA = codeSeries(a.seriesName);
        const codeB = codeSeries(b.seriesName);
        if (codeA === codeB) return 0;
        if (codeA === null) return 1;
        if (codeB === null) return -1;
        return codeA - codeB;
      });

      // Monta objeto final
      output.push({
        materialUseName: tipo,
        produtoTotal: itens.length,
        acv,
        series,
      });
    };


    // Processa alunos e professores
    processar("Aluno");
    processar("Professor");

    return output;
  }

  const filteredProducts = managedProducts.filter((product) => {
    const term = debouncedSearchTermProduct.toLowerCase();
    return (
      product.productCode?.toLowerCase().includes(term)
      || product.materialUseName?.toLowerCase().includes(term)
      || (product as any).productSku?.toLowerCase().includes(term)
      || (product as any).segmentName?.toLowerCase().includes(term)
      || (product as any).seriesName?.toLowerCase().includes(term)
      || (product as any).productDescription?.toLowerCase().includes(term)
    )
  });

  // Uso:
  const output = agruparPorUso(filteredProducts);



  const handleRemoveProductWithComment = async (product: any, motivo: string) => {
    try {
      // 1. Remover do banco de dados
      const { error: deleteError } = await profileClientApi.profileClientDeactivate(product.profileClientId, { content: motivo });

      if (deleteError) throw deleteError;

      // 2. Atualizar estado local apenas se o banco foi atualizado com sucesso
      setManagedProducts(currentProducts =>
        currentProducts.map(p =>
          p.profileClientId === product.profileClientId
            ? { ...p, isActive: false }
            : p
        )
      );

      // 3. Log da atividade
      // await logProductRemoved(clientCode, clientName, product, motivo);

      // 4. Fechar dialog e mostrar toast de sucesso
      setRemovingProduct(null);
      toast.success('Produto removido!', {
        description: `${product.productDescription} foi removido com sucesso.`,
      });
    } catch (error: any) {
      console.error('Erro ao remover produto:', error);
      const backendMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Tente novamente.";
      toast.error('Erro ao remover produto', {
        description: backendMessage
      });
    }
  };

  const handleSaveProduct = async (updated: any) => {
    try {

      if (updated.comentario.length <= 0) {
        toast.warning('Comentário inválido', {
          description: "O campo comentário é obrigatório."
        });
        setIsLoading(false);
        return
      }

      await clientService.updateClientProduct(updated.id, {
        contractualQuantity: updated.quantidade_contratual,
        discount: updated.desconto,
        adjustment: updated.desconto,
        content: updated.comentario,
        productId: updated.productId,
        productPrice: updated.preco ?? 0,
      });

      // ATUALIZAÇÃO LOCAL OTIMISTA DO ESTADO
      setManagedProducts(currentProducts =>
        currentProducts.map(p =>
          p.profileClientId === updated.id
            ? {
              ...p,
              contractualQuantity: updated.quantidade_contratual,
              discount: updated.desconto,
              productPrice: updated.preco ?? p.productPrice,
              priceWithDiscount: updated.preco ?? p.productPrice,
              priceWithoutDiscount: updated.preco ?? p.priceWithoutDiscount ?? p.productPrice,
              totalWithDiscount: (updated.preco ?? p.priceWithoutDiscount ?? p.productPrice) * (updated.quantidade_contratual ?? p.contractualQuantity),
              totalWithoutDiscount: (updated.preco ?? p.priceWithoutDiscount ?? p.productPrice) * (updated.quantidade_contratual ?? p.contractualQuantity),
            }
            : p
        )
      );

      // Recarrega a lista de produtos a partir da API para refletir o estado real do backend
      onProductUpdate();

      toast.success('Produto atualizado!');
    } catch (error) {
      console.error("Erro ao atualizar produto:", error);
    } finally {
      setIsEditOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleSaveShippingFee = async () => {
    setIsLoading(true); // Use the existing isLoading state
    try {
      const fee = parseFloat(shippingFee.replace(',', '.')); // Handle comma as decimal separator
      if (isNaN(fee) || fee < 0 || fee > 100) {
        toast.warning('Valor inválido', { description: "A taxa de frete deve ser um número entre 0 e 100." });
        return;
      }
      // Use the existing clientsApi.update function
      await clientsApi.update(managedClient.id, { shippingFee: fee, comment: shippingFeeComment });

      setManagedClient(currentClient => ({
        ...currentClient,
        shipping_fee: fee,
      }));

      toast.success("Sucesso!", { description: "A taxa de frete foi atualizada." });
      // onProductUpdate(); // This is no longer needed
    } catch (error: any) {
      console.error("Erro ao salvar taxa de frete:", error);
      const backendMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Não foi possível atualizar a taxa de frete.";
      toast.error("Erro ao salvar", { description: backendMessage });
    } finally {
      setIsLoading(false);
      setIsShippingFeeDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <p className="text-center text-gray-500 py-6">Carregando produtos...</p>
    );
  }

  const selectedYearNumber = parseInt(adjustmentFormYear, 10);
  const selectedYearRecord = clientAdjustments.find(
    (a) => (a.cycleYear ?? a.year) === selectedYearNumber
  );
  const isPastYear = !isNaN(selectedYearNumber) && selectedYearNumber < currentYear;

  return (
    <div className="space-y-8">
      {/* Gerenciar Parâmetros do Cliente (Frete, Plano de Pagamentos & Reajuste) */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Taxa de Frete */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-card">
          <div>
            <h4 className="font-semibold">Taxa de Frete</h4>
            <p className="text-sm text-muted-foreground">
              Taxa atual: <span className="font-bold text-primary">{managedClient.shipping_fee?.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 }) || "0,00"}%</span>
            </p>
          </div>
          <Button variant="outline" onClick={() => setIsShippingFeeDialogOpen(true)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        </div>

        {/* Plano de Pagamentos */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-card">
          <div>
            <h4 className="font-semibold">Plano de Pagamentos</h4>
            <p className="text-sm text-muted-foreground">
              Plano atual:{" "}
              {currentPaymentPlan ? (
                <span className="font-bold text-green-600">{currentPaymentPlan.planName}</span>
              ) : (
                <span className="font-bold text-muted-foreground">Não definido</span>
              )}
            </p>
            {isCustomPaymentPlan(currentPaymentPlan) && (managedClient.paymentConfigurationIds?.length ?? 0) > 0 && (
              <Badge variant="outline" className="mt-2 w-fit text-xs bg-slate-50 text-slate-600 border-slate-200">
                Configurações selecionadas: {managedClient.paymentConfigurationIds?.length}
              </Badge>
            )}
          </div>
          <Button variant="outline" aria-label="Editar plano de pagamentos" onClick={() => setIsPaymentPlanDialogOpen(true)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        </div>

        {/* Chamado no Zendesk ao criar pedido */}
        {isNewOrderZendeskTicketEnabled && isNewOrderZendeskTicketClientCardVisible && (
          <div className="flex items-center justify-between rounded-lg border p-4 bg-card">
            <div>
              <h4 className="font-semibold">Chamado no Zendesk ao criar pedido</h4>
              <p className="text-sm text-muted-foreground">
                Situação atual:{" "}
                {managedClient.createZendeskTicketOnOrder ? (
                  <span className="font-bold text-green-600">Ativado</span>
                ) : (
                  <span className="font-bold text-muted-foreground">Desativado</span>
                )}
              </p>
            </div>
            <Button variant="outline" aria-label="Editar chamado no Zendesk ao criar pedido" onClick={() => setIsZendeskTicketDialogOpen(true)}>
              <Edit3 className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Reajuste do Cliente */}
        <div className="flex items-center justify-between rounded-lg border p-4 bg-card">
          <div>
            <h4 className="font-semibold flex items-center gap-2">
              Reajuste do Cliente
              {(() => {
                const lastAdj = clientAdjustments.length > 0
                  ? [...clientAdjustments].sort((a, b) => (b.cycleYear ?? b.year) - (a.cycleYear ?? a.year))[0]
                  : null;
                return lastAdj ? (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Ciclo {lastAdj.cycleYear ?? lastAdj.year}</Badge>
                ) : null;
              })()}
            </h4>
            <p className="text-sm text-muted-foreground">
              Reajuste atual:{" "}
              <span className="font-bold text-blue-600">
                {(() => {
                  const lastAdj = clientAdjustments.length > 0
                    ? [...clientAdjustments].sort((a, b) => (b.cycleYear ?? b.year) - (a.cycleYear ?? a.year))[0]
                    : null;
                  return lastAdj
                    ? lastAdj.adjustment.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 })
                    : "0,00";
                })()}%
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsHistoryDialogOpen(true)}>
              <History className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={handleOpenAdjustmentModal}>
              <Edit3 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Modal Taxa de Frete */}
      <Dialog open={isShippingFeeDialogOpen} onOpenChange={setIsShippingFeeDialogOpen}>
        <DialogContent className="sm:max-w-[600px] p-0">
          <Card className="overflow-hidden border-none">
            <CardHeader className="bg-muted/20">
              <CardTitle>Gerenciar Taxa de Frete</CardTitle>
              <CardDescription>
                Ajuste a taxa de frete para este cliente. Um comentário é obrigatório para cada alteração.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="shipping-fee-modal">Taxa de Frete (%)</Label>
                  <div className="relative">
                    <Input
                      id="shipping-fee-modal"
                      type="number"
                      value={shippingFee}
                      onChange={(e) => setShippingFee(e.target.value)}
                      className="pr-8"
                      placeholder="Ex: 5.5"
                    />
                    <span className="absolute inset-y-0 right-3 flex items-center text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="space-y-2 text-left md:text-right">
                  <Label>Taxa Atual</Label>
                  <p className="text-2xl font-bold text-primary">
                    {managedClient.shipping_fee?.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 }) || "0,00"}%
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="shipping-fee-comment-modal">
                  Comentário de Alteração
                </Label>
                <Textarea
                  id="shipping-fee-comment-modal"
                  value={shippingFeeComment}
                  onChange={(e) => setShippingFeeComment(e.target.value)}
                  placeholder="Digite o motivo da alteração..."
                  disabled={shippingFee === (managedClient.shipping_fee?.toString() || "0")}
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
            <CardFooter className="border-t bg-muted/20 p-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsShippingFeeDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveShippingFee}
                disabled={
                  isLoading ||
                  shippingFee === (managedClient.shipping_fee?.toString() || "0") ||
                  !shippingFeeComment.trim()
                }
              >
                {isLoading ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>

      {isNewOrderZendeskTicketEnabled && isNewOrderZendeskTicketClientCardVisible && (
        <ZendeskTicketOnOrderDialog
          isOpen={isZendeskTicketDialogOpen}
          onClose={() => setIsZendeskTicketDialogOpen(false)}
          onSave={handleSaveZendeskTicketOnOrder}
          clientName={managedClient.name}
          currentValue={managedClient.createZendeskTicketOnOrder ?? false}
        />
      )}

      <PaymentPlanDialog
        isOpen={isPaymentPlanDialogOpen}
        onClose={() => setIsPaymentPlanDialogOpen(false)}
        onSave={handleSavePaymentPlan}
        clientName={managedClient.name}
        currentPaymentPlanId={managedClient.paymentPlanId}
        currentPaymentConfigurationIds={managedClient.paymentConfigurationIds}
        paymentPlans={paymentPlans}
        paymentConfigurations={paymentConfigurations}
        isLoading={isLoadingPaymentPlans || isLoadingPaymentConfigurations}
      />

      {/* Modal Reajuste do Cliente */}
      <Dialog open={isAdjustmentDialogOpen} onOpenChange={setIsAdjustmentDialogOpen}>
        <DialogContent className="sm:max-w-[600px] p-0">
          <Card className="overflow-hidden border-none">
            <CardHeader className="bg-muted/20">
              <CardTitle>Gerenciar Reajuste do Cliente</CardTitle>
              <CardDescription>
                Ajuste o percentual de reajuste no contexto do cliente para o ciclo selecionado.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="adjustment-year-modal">Ano do Ciclo</Label>
                  <Input
                    id="adjustment-year-modal"
                    type="number"
                    value={adjustmentFormYear}
                    onChange={(e) => setAdjustmentFormYear(e.target.value)}
                    className={isPastYear ? "border-red-500 text-red-600 focus-visible:ring-red-500" : ""}
                    placeholder="Ex: 2026"
                  />
                  {isPastYear && (
                    <p className="text-xs text-red-600 font-medium mt-1">
                      O Ano do Ciclo não pode ser menor que o ano atual ({currentYear}).
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adjustment-fee-modal">Reajuste (%)</Label>
                  <div className="relative">
                    <Input
                      id="adjustment-fee-modal"
                      type="number"
                      step="0.01"
                      value={adjustmentFormValue}
                      onChange={(e) => {
                        setAdjustmentFormValue(e.target.value);
                      }}
                      className={`pr-8 ${!isPastYear && parseFloat(adjustmentFormValue) < 0 ? 'border border-destructive' : ''}`}
                      placeholder="Ex: 5.5"
                      disabled={isPastYear}
                    />
                    {!isPastYear && parseFloat(adjustmentFormValue) < 0 && (
                      <p className="text-sm text-destructive mt-1">O valor não pode ser negativo</p>
                    )}
                    <span className="absolute inset-y-0 right-3 flex items-center text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
              {/* <div className="space-y-2 text-left">
                <Label>Reajuste Atual do Ciclo ({adjustmentFormYear || "—"})</Label>
                {selectedYearRecord ? (
                  <p className="text-2xl font-bold text-blue-600">
                    {selectedYearRecord.adjustment?.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 })}%
                  </p>
                ) : (
                  <p className="text-sm font-medium text-muted-foreground bg-muted/40 p-2.5 rounded border">
                    Nenhum registro encontrado no banco para o ciclo {adjustmentFormYear || "informado"}
                  </p>
                )}
              </div> */}
              <div className="space-y-2">
                <Label htmlFor="adjustment-comment-modal">
                  Comentário / Motivo do Reajuste <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="adjustment-comment-modal"
                  value={adjustmentFormComment}
                  onChange={(e) => setAdjustmentFormComment(e.target.value)}
                  placeholder="Digite o motivo da alteração do reajuste..."
                  className={`min-h-[80px] ${!adjustmentFormComment.trim() && isAdjustmentDialogOpen && !isPastYear ? 'border border-destructive' : ''}`}
                  disabled={isPastYear}
                />
                {!adjustmentFormComment.trim() && isAdjustmentDialogOpen && !isPastYear && (
                  <p className="text-sm text-destructive mt-1">Campo obrigatório</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t bg-muted/20 p-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setIsAdjustmentDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveClientAdjustment}
                disabled={
                  isLoadingAdjustment ||
                  isPastYear ||
                  !adjustmentFormValue.trim() ||
                  !adjustmentFormYear.trim() ||
                  !adjustmentFormComment.trim() ||
                  parseFloat(adjustmentFormValue) < 0
                }
              >
                {isLoadingAdjustment ? "Salvando..." : "Salvar Reajuste"}
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>

      {/* Modal Histórico de Reajustes do Cliente */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="sm:max-w-[650px] p-0">
          <Card className="overflow-hidden border-none">
            <CardHeader className="bg-muted/20">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-blue-600" />
                Histórico de Reajustes do Cliente
              </CardTitle>
              <CardDescription>
                Histórico de reajustes contratuais por ciclo registrados para {managedClient.name}.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {clientAdjustments.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">
                  Nenhum histórico de reajuste registrado para este cliente.
                </p>
              ) : (
                <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                  {clientAdjustments.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="border rounded-lg p-4 bg-muted/20 flex flex-col gap-2 hover:bg-muted/40 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-semibold">
                            Ciclo {item.cycleYear}
                          </Badge>
                        </div>
                        <span className="text-lg font-bold text-blue-600">
                          {item.adjustment?.toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 })}%
                        </span>
                      </div>
                      {item.comment && (
                        <div className="text-sm text-muted-foreground bg-background/60 p-2.5 rounded border text-gray-700">
                          <span className="font-semibold text-xs uppercase text-muted-foreground block mb-0.5">Motivo / Comentário</span>
                          {item.comment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter className="border-t bg-muted/20 p-4 flex justify-end">
              <Button variant="ghost" onClick={() => setIsHistoryDialogOpen(false)}>
                Fechar
              </Button>
            </CardFooter>
          </Card>
        </DialogContent>
      </Dialog>



      {/* Lista de produtos */}
      <div>
        <div className="flex items-center justify-between mx-2">
          <h4 className="text-lg mb-6 font-semibold">Produtos Vinculados</h4>
          <Button
            onClick={() => navigate(`/backoffice/perfil-clientes/${client.id}/gerenciar-produtos`, {
              state: {
                client: {
                  id: client.id,
                  name: client.name,
                  code: (client as any).code,
                  city: (client as any).city,
                  state: (client as any).state,
                  studentCount: (client as any).studentCount,
                  shipping_fee: client?.shipping_fee || 0,
                }
              }
            })}
            className="gap-2"
            size="sm"
          >
            <Plus className="h-4 w-4" />
            Adicionar/Remover
          </Button>
        </div>
        <div className="space-y-4 my-4 mx-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, SKU, segmento ou série..."
              value={searchTermProduct}
              onChange={(e) => setSearchTermProduct(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTermProduct && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full"
                onClick={() => setSearchTermProduct("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="grid gap-4 mt-4">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {output.length > 0 && output.map(m => (
              <AccordionItem
                key={m.materialUseName}
                value={m.materialUseName}
                className="border w-full border-gray-200 rounded-xl transition-all duration-300 hover:shadow-sm data-[state=open]:bg-blue-50/50"
              >
                <AccordionTrigger asChild className="w-full p-6 text-lg cursor-pointer font-semibold text-gray-800 hover:no-underline">
                  <div className="flex justify-between items-center  w-full mr-3">
                    <div className="flex items-center gap-4">
                      <GraduationCap className="h-5 w-5 text-blue-500" />
                      <span>{m.materialUseName}</span>
                      <Badge variant="secondary" className="text-sm">
                        {m.produtoTotal} produtos
                      </Badge>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        ACV Total
                      </span>
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(m.acv)}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-6 pt-0 grid gap-3 max-h-[800px] overflow-y-auto">
                  <Accordion type="single" collapsible className="w-full space-y-3">
                    {m.series.length > 0 && m.series.map((group: any) => (
                      <AccordionItem
                        value={group.seriesName}
                        key={group.seriesName}
                        className="border w-full border-gray-200 rounded-xl transition-all duration-300 hover:shadow-sm data-[state=open]:bg-gray-100/50"
                      >
                        <AccordionTrigger asChild className="w-full cursor-pointer p-6 text-lg font-semibold text-gray-800 hover:no-underline">
                          <div className="flex justify-between items-center w-full mr-3">
                            <div className="flex items-center gap-4">
                              <span>{group.seriesName}</span>
                              <Badge variant="secondary" className="text-sm">
                                {group.dados.length} produtos
                              </Badge>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-sm font-medium text-muted-foreground">
                                ACV da Série
                              </span>
                              <span className="text-xl font-bold text-green-600">
                                {formatCurrency(group.totalWithDiscount)}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="p-6 pt-0">
                          <div className="grid gap-3">
                            {group.dados.map((p: any, index) => p.isActive && (
                              <Card
                                key={`${p.profileClientId}-${index}`}
                                className="border border-gray-200 rounded-xl hover:shadow-sm transition-all duration-200"
                              >
                                <CardContent className="flex justify-between items-center py-4 px-6">
                                  <div>
                                    <h4 className="font-semibold">
                                      {p.productDescription}

                                      {p.productSku && (
                                        <Badge variant="outline" className="mx-2 text-xs bg-gray-50 text-gray-700 border-gray-200">
                                          SKU: {p.productSku}
                                        </Badge>
                                      )}
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                      {p.segmentName} • {p.seriesName} • {p.materialUseName}
                                    </p>
                                    <p className="text-sm mt-1">
                                      Qtd:{" "}
                                      <span className="font-medium">{p.contractualQuantity}</span>
                                    </p>
                                  </div>

                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => {
                                      setSelectedProduct(p);
                                      setIsEditOpen(true);
                                    }}
                                  >
                                    <Edit3 className="h-4 w-4 text-blue-600" />
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {output.length === 0 && managedProducts.length > 0 && (
            <div className="text-center py-10">
              <Search className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">Nenhum produto encontrado</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Tente ajustar seus termos de busca.
              </p>
            </div>
          )}

          {managedProducts.length === 0 && (
            <div className="text-center py-10">
              <Package className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">Nenhum produto vinculado</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Adicione produtos a este cliente para vê-los aqui.
              </p>
            </div>
          )}
        </div>
      </div>



      {/* Modal de edição */}
      {selectedProduct && (
        <EditProductDialog
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSave={handleSaveProduct}
          product={{
            id: selectedProduct.profileClientId,
            productId: selectedProduct.productId,
            codigo_cnx: managedClient.name,
            nome_cliente: managedClient.name,
            codigo_produto: selectedProduct.productCode,
            descricao_produto: selectedProduct.productDescription,
            utilizacao: selectedProduct.materialUseName,
            ano_letivo: parseInt(selectedProduct.productSchoolYear),
            serie: selectedProduct.seriesName,
            segmento: selectedProduct.segmentName,
            quantidade_contratual: selectedProduct.contractualQuantity,
            desconto: selectedProduct.discount,
            preco: selectedProduct.priceWithoutDiscount ?? selectedProduct.productPrice,
            habilitado: true,
          }}
          existingProductCodes={managedProducts.map((p) => p.productCode)}
        />
      )}

      <RemoveProductDialog
        isOpen={!!removingProduct}
        onClose={() => setRemovingProduct(null)}
        onConfirm={handleRemoveProductWithComment}
        product={removingProduct}
        client={managedClient}
      />
    </div>
  );
}

